import { makeStyles, tokens } from '@fluentui/react-components';
import React, { useMemo } from 'react';
import { ContentMetadata } from '../../../libs/models/ContentMetadata';
import { useTranslation } from 'react-i18next';
import { computeCitationView } from '../../utils/ChatMessageUtils';
import { isSafeUrl, sanitizeTitle } from './sourceUtils';
import { withReferralParams } from '../../../libs/utils/referralTracking';

const useClasses = makeStyles({
    content: {
        wordBreak: 'break-word',
        '& a': {
            color: tokens.colorBrandForegroundLink,
        },
        '& a:hover': {
            color: tokens.colorBrandForegroundLinkHover,
        },
    },
});

interface ChatHistorySourceLinksProps {
    contentMetadata: undefined | ContentMetadata[];
}

export const ChatHistorySourceLinks: React.FC<ChatHistorySourceLinksProps> = ({ contentMetadata }) => {
    const classes = useClasses();
    const { t } = useTranslation();
    // Dedup entries that share a URL so atomic-flow results don't render the same help-center
    // page twice. computeCitationView keeps the matching superscript-to-row mapping used by
    // formatChatMessage in sync — inline `²` and footer `2)` always point at the same row.
    const { visibleEntries } = useMemo(() => computeCitationView(contentMetadata), [contentMetadata]);
    return (
        <div className={classes.content}>
            <div className={classes.content}>
                <b> {t('chathistorysourcelinks.references')}:</b>
                {visibleEntries.map((data) => {
                    const cleanTitle = sanitizeTitle(data.title);
                    // Single sanitized sink reused by both branches, so there's one
                    // dangerouslySetInnerHTML to audit rather than two near-identical ones.
                    const titleNode = <span dangerouslySetInnerHTML={{ __html: cleanTitle }} />;
                    return (
                        <p key={data.displayOrder}>
                            {data.displayOrder}){' '}
                            {isSafeUrl(data.url) ? (
                                <a
                                    href={withReferralParams(data.url, 'citation')}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    {titleNode}
                                </a>
                            ) : (
                                titleNode
                            )}
                            <br></br>
                        </p>
                    );
                })}
            </div>
        </div>
    );
};
